/**
 * Auto-reload once when a dynamic-import chunk or hashed dist/ asset
 * fails to load. Parcel rewrites all code-split chunks to hashed
 * filenames (see package.json parcel-namer-rewrite), and Pagely
 * deploys replace dist/ wholesale — so tabs open across a deploy,
 * or pages served from a stale Cloudflare HTML cache, reference
 * hashes that no longer exist on disk.
 *
 * Gated by sessionStorage so a genuinely missing chunk can't loop.
 *
 * Duplicate-event suppression for the $exception kill metric lives in
 * util/posthog.js's `before_send` hook, which calls `shouldDropChunkException`
 * (exported below). posthog-js captures unhandled rejections via
 * `window.onunhandledrejection` (property assignment) rather than
 * addEventListener, so preventDefault/stopImmediatePropagation in the
 * listeners here cannot stop PostHog from sending the event — only
 * before_send can. See MTN-228 / MTN-366.
 */

import {
  armChunkReload,
  hasReloaded,
  isStaleChunkException,
} from "./chunk-reload-policy";
import { capture } from "./posthog";

export { shouldDropChunkException } from "./chunk-reload-policy";

const DIST_ASSET =
  /\/[dD][iI][sS][tT]\/[^/?#]+\.(?:[jJ][sS]|[mM][jJ][sS]|[cC][sS][sS])(?:\?|#|$)/u;

const reload = (reason) => {
  if (!armChunkReload()) {
    return false;
  }

  try {
    capture("chunk_reload_triggered", { reason });
  } catch {
    // Swallow — we're reloading anyway.
  }

  // Small delay so the capture request has a chance to leave the tab.
  setTimeout(() => window.location.reload(), 50);

  return true;
};

export const install = () => {
  window.addEventListener("unhandledrejection", (event) => {
    const message = event.reason?.message ?? event.reason ?? "";

    if (!isStaleChunkException(message)) {
      return;
    }

    // Silence the browser's "Unhandled Promise Rejection" console warning.
    // Does NOT stop posthog-js (it captures via window.onunhandledrejection,
    // a property assignment) — that suppression is in before_send via
    // shouldDropChunkException above.
    event.preventDefault();

    if (hasReloaded()) {
      return;
    }

    reload("unhandledrejection");
  });

  // <link>/<script> load failures don't bubble — listen on capture phase.
  // posthog-js's autocapture doesn't observe these (window.onerror only fires
  // for script execution errors, not resource load failures), so this listener
  // needs no suppression beyond the reload-loop guard.
  window.addEventListener(
    "error",
    (event) => {
      const { target } = event;

      if (!target || target === window) {
        return;
      }

      const url = target.href ?? target.src ?? "";

      if (!DIST_ASSET.test(url)) {
        return;
      }

      if (hasReloaded()) {
        return;
      }

      reload("resource_error");
    },
    true
  );
};
